import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type ElementType, type CSSProperties } from "react";

/**
 * Lightweight scroll reveal (no animation library; keeps above-the-fold JS small).
 * Server HTML and first client render are a plain, fully visible element.
 * After hydration, only elements below the fold switch to the fade-in; elements
 * already on screen (e.g. hero headings) stay static so LCP is never delayed.
 */
interface RevealProps {
  children: ReactNode;
  className?: string;
  id?: string;
  delay?: number;
  /** When true, staggers RevealItem children */
  stagger?: boolean;
  /** Stagger delay in seconds */
  staggerDelay?: number;
  as?: "div" | "section" | "ul" | "ol" | "li";
}

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** Fallback: never leave content hidden if the in-view trigger fails. */
const FALLBACK_MS = 2500;

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function Reveal({
  children,
  className,
  id,
  delay = 0,
  stagger = false,
  staggerDelay = 0.06,
  as = "div",
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<"static" | "hidden" | "shown">("static");

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined" || prefersReducedMotion()) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;
    if (stagger) {
      el.querySelectorAll<HTMLElement>("[data-reveal-item]").forEach((item, i) => {
        item.style.transitionDelay = `${i * staggerDelay}s`;
      });
    }
    setState("hidden");
  }, []);

  useEffect(() => {
    if (state !== "hidden" || !ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setState("shown");
        io.disconnect();
      }
    });
    io.observe(ref.current);
    const t = window.setTimeout(() => setState("shown"), FALLBACK_MS);
    return () => {
      io.disconnect();
      window.clearTimeout(t);
    };
  }, [state]);

  const Tag = as as ElementType;
  const style: CSSProperties | undefined =
    !stagger && state !== "static" && delay ? { transitionDelay: `${delay}s` } : undefined;
  const cls = stagger
    ? className
    : [className, state === "hidden" && "reveal-anim reveal-hidden", state === "shown" && "reveal-anim"]
        .filter(Boolean)
        .join(" ") || undefined;

  return (
    <Tag ref={ref} id={id} className={cls} style={style} data-reveal={stagger && state !== "static" ? state : undefined}>
      {children}
    </Tag>
  );
}

/** Child of a stagger Reveal. Visible unless its parent is animating. */
export function RevealItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={className} data-reveal-item="">
      {children}
    </div>
  );
}
